import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Button, Icon, Label } from '@iq/react-components';
import JsonEditor from '../../../JsonEditor';
import { filterMapping } from '../constants';

const ClassificationFilter = ({ selectedFilters, setSelectedFilters }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const getFormDataFromFilters = useCallback((filters) => {
    return Object.fromEntries(
      Object.entries(filterMapping).map(([key, value]) => [key, filters.includes(value)])
    );
  }, []);

  const getFiltersFromFormData = useCallback((formData) => {
    return Object.entries(filterMapping)
      .filter(([key]) => formData[key])
      .map(([, value]) => value);
  }, []);

  const [localFilters, setLocalFilters] = useState(() => getFormDataFromFilters(selectedFilters));

  useEffect(() => {
    if (open) {
      setLocalFilters(getFormDataFromFilters(selectedFilters));
    }
  }, [open, selectedFilters, getFormDataFromFilters]);

  const handleClickOutside = useCallback((event) => {
    if (!ref.current) {
      return;
    }

    const targetIsInsideReference = ref.current.contains(event.target);

    if (!targetIsInsideReference) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('click', handleClickOutside, true);
    } else {
      document.removeEventListener('click', handleClickOutside, true);
    }
  }, [open]);

  const onFiltersApply = () => {
    setSelectedFilters(getFiltersFromFormData(localFilters));
    setOpen(false);
  };

  const onFiltersReset = () => {
    setSelectedFilters([]);
    setOpen(false);
  };

  const onFormChange = (event) => {
    setLocalFilters(event.formData.classifications);
  };

  return (
    <div
      ref={ref}
      className={`classification-filters-component`}
    >
      <div
        className="header"
        onClick={() => setOpen(!open)}
      >
        <div className="label">Classification</div>
        <div className="icon-container">
          <Icon
            icon="he-down"
            size="xs"
          />
        </div>
      </div>
      {open && (
        <div className="content">
          <div className="actions">
            <Label>Filters</Label>
            <div className="actions-button">
              <Button
                onClick={onFiltersReset}
                activity="secondary"
                slim
              >
                Reset
              </Button>
              <Button
                slim
                onClick={onFiltersApply}
              >
                Apply
              </Button>
            </div>
          </div>
          <div className="body">
            <div className="classifications">
              <div>
                <JsonEditor
                  formData={{ classifications: localFilters }}
                  scrollIntoView={false}
                  onFormChange={onFormChange}
                  schema={{
                    type: 'object',
                    properties: {
                      classifications: {
                        type: 'object',
                        title: 'Classification',
                        properties: {
                          showNoClassification: {
                            type: 'boolean',
                            title: 'No classification',
                            default: true,
                          },
                          showLikelyTrip: {
                            type: 'boolean',
                            title: 'Likely trip',
                            default: true,
                          },
                          showLikelyEnergization: {
                            type: 'boolean',
                            title: `Likely energization`,
                            default: true,
                          },
                          showLikelyDeEnergization: {
                            type: 'boolean',
                            title: `Likely de-energization`,
                            default: true,
                          },
                          showLikelyFalseAlarm: {
                            type: 'boolean',
                            title: `Likely false alarm`,
                            default: true,
                          },
                          showCFLikelyACError: {
                            type: 'boolean',
                            title: `CF - likely AC error`,
                            default: true,
                          },
                          showCFLikelyNotACError: {
                            type: 'boolean',
                            title: `CF - likely not AC error`,
                            default: true,
                          },
                        },
                      },
                    },
                  }}
                  initialEditMode={true}
                  showEditButton={false}
                  showButtons={false}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassificationFilter;
